import React from "react";
import styled, { css } from "styled-components";

import { breakpoints, colors, fontFamily, sectionRhythm } from "../../../theme";
import { PROJECT_SHAKESPEERS } from "../../utils/constants/projects";
import ContactMeSection from "../contact-me-section";
import Testimonials from "../reviews-section/Testimonials";
import ContainerScroll from "../../components/container-scroll";
import { ProjectHero, caseStudyBodyStyles } from "./project-page-media";
import { POINTER_HOVER_MEDIA } from "../../styles/pointer-hover";
import { getLinkClassName } from "../../styles/global-styles";

const PageContainer = styled.div`
  width: calc(100vw - 48px);
  padding-top: ${sectionRhythm.paddingY.mobile}px;
  margin: 0 auto;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    width: 65%;
    max-width: 800px;
    padding-top: ${sectionRhythm.paddingY.desktop}px;
    margin: 0 auto;
  }
  @media only screen and (min-width: 900px) {
    width: 60%;
  }
`;

const Title = styled.h1`
  margin: 0 0 16px;
  font-family: ${fontFamily.extraBold};
  font-size: 24px;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    font-size: 44px;
    margin: 0 0 24px;
  }
`;

const OverviewSection = styled.div`
  margin-top: 2rem;
  margin-bottom: 0;
  display: grid;
  gap: 1.5rem;
`;

const OverviewRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
`;

const OverviewItem = styled.div`
  flex: 1;
  min-width: 140px;
`;

const OverviewLabel = styled.div`
  font-size: 14px;
  color: ${colors.grey.darkMedium};
  margin-bottom: 4px;
`;

const OverviewValue = styled.div`
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;

  a {
    color: inherit;
    word-break: break-all;

    @media ${POINTER_HOVER_MEDIA} {
      &:hover {
        color: ${colors.linkHover.light};
        text-decoration: underline;
      }
    }
  }
`;

const Body = styled.div`
  ${caseStudyBodyStyles}
`;

const ContentImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
  object-fit: contain;
  border-radius: 18px;
  background: ${colors.white};
  margin: 1.5rem 0;
`;

const StackSubheading = styled.h5`
  && {
    margin: 0 0 0.75rem;
    padding-top: ${(props) => (props.$spaced ? "2rem" : "0")};
  }
`;

const boxBorder = colors.grey.medium;

const boxHelperTypography = css`
  font-size: 14px;
  line-height: 1.45;
  font-weight: 600;
  color: ${colors.grey.darkMedium};
`;

const diagramHelperText = css`
  ${boxHelperTypography}
  display: block;
`;

const DecisionFlow = styled.div`
  margin: 2rem auto 2.5rem;
  display: grid;
  gap: 1rem;
  max-width: 420px;
`;

const DecisionNode = styled.div`
  padding: 1rem;
  border: 1px solid ${boxBorder};
  border-radius: 8px;
  background: ${colors.white};
  text-align: center;
  box-sizing: border-box;

  strong {
    display: block;
    margin-bottom: 0.25rem;
    font-size: 14px;
    font-weight: 700;
  }

  small {
    ${diagramHelperText}
  }
`;

const DecisionArrow = styled.div`
  width: 1px;
  height: 24px;
  margin: 0 auto;
  background: black;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 50%;
    width: 7px;
    height: 7px;
    border-right: 1px solid black;
    border-bottom: 1px solid black;
    transform: translateX(-50%) rotate(45deg);
    background: transparent;
  }
`;

const DecisionOptions = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  margin: 2rem 0;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    grid-template-columns: repeat(
      ${(props) => props.$columns || 2},
      minmax(0, 1fr)
    );
  }
`;

const DecisionOption = styled.div`
  padding: 1.25rem;
  border: 1px solid ${boxBorder};
  border-radius: 10px;
  background: ${colors.white};
  box-sizing: border-box;

  h6 {
    margin: 0 0 0.75rem;
    font-family: ${fontFamily.extraBold};
    font-size: 15px;
    line-height: 1.3;

    @media only screen and (min-width: ${breakpoints.tablet}) {
      font-size: 16px;
    }
  }

  ul,
  ol {
    margin: 0;
    padding-left: 1.25rem;
    list-style: none;
  }

  li {
    ${boxHelperTypography}
    display: list-item;
    margin: 0 0 0.45rem 1rem;
    padding: 0;
    letter-spacing: -0.003em;
    list-style-position: outside;

    @media only screen and (min-width: ${breakpoints.tablet}) {
      font-size: 14px;
      line-height: 1.45;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  ul > li {
    list-style-type: disc;
  }

  ol > li {
    list-style-type: decimal;
  }
`;

const DecisionChoice = styled.div`
  margin: 1.5rem 0 2rem;
  padding: 1.25rem;
  border: 1px solid ${boxBorder};
  border-radius: 10px;
  background: ${colors.surface};
  box-sizing: border-box;

  strong {
    display: block;
    margin-bottom: 0.35rem;
    font-family: ${fontFamily.extraBold};
    font-size: 15px;
    line-height: 1.3;

    @media only screen and (min-width: ${breakpoints.tablet}) {
      font-size: 16px;
    }
  }

  small {
    ${diagramHelperText}
  }
`;

const ConstraintGrid = styled.div`
  display: grid;
  gap: 1rem;
  margin: 1.5rem 0 2.5rem;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const ConstraintCard = styled.div`
  padding: 1.25rem;
  border: 1px solid ${boxBorder};
  border-radius: 10px;
  background: ${colors.white};
  box-sizing: border-box;

  strong {
    display: block;
    margin-bottom: 0.35rem;
    font-family: ${fontFamily.extraBold};
    font-size: 15px;
    line-height: 1.3;

    @media only screen and (min-width: ${breakpoints.tablet}) {
      font-size: 16px;
    }
  }

  small {
    ${diagramHelperText}
  }
`;

const PersonaGrid = styled.div`
  display: grid;
  gap: 1rem;
  margin: 1.5rem 0 2.5rem;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const PersonaCard = styled.div`
  padding: 1.25rem;
  border: 1px solid ${boxBorder};
  border-radius: 10px;
  background: ${colors.white};
  box-sizing: border-box;

  strong {
    display: block;
    margin-bottom: 0.35rem;
    font-size: 14px;
    font-weight: 700;
  }

  small {
    ${diagramHelperText}
  }
`;

const ArchitectureDiagram = styled.div`
  margin: 2.5rem 0 3rem;
  padding: 1.25rem 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
  box-sizing: border-box;
`;

const ArchitectureNode = styled.div`
  width: 100%;
  max-width: 280px;
  margin: 0 auto;
  padding: 0.9rem 1rem;
  border: 1px solid ${boxBorder};
  border-radius: 8px;
  text-align: center;
  background: ${colors.white};
  box-sizing: border-box;

  strong {
    display: block;
    font-size: 14px;
    font-weight: 700;
  }

  small {
    ${diagramHelperText}
    margin-top: 3px;
  }
`;

const ArchitectureArrow = styled(DecisionArrow)`
  height: 28px;
  flex-shrink: 0;
`;

const ArchitectureSplitArrow = styled.div`
  position: relative;
  width: 100%;
  height: 28px;
  flex-shrink: 0;
  align-self: stretch;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 50%;
    width: 1px;
    height: 100%;
    background: black;
  }

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 50%;
    width: 7px;
    height: 7px;
    border-right: 1px solid black;
    border-bottom: 1px solid black;
    transform: translateX(-50%) rotate(45deg);
    background: transparent;
  }

  span {
    display: none;
  }

  @media only screen and (min-width: ${breakpoints.tablet}) {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 2.5rem;
    height: 56px;

    &::before {
      height: 28px;
    }

    &::after {
      top: 28px;
      bottom: auto;
      left: calc((100% - 5rem) / 6);
      right: calc((100% - 5rem) / 6);
      width: auto;
      height: 1px;
      border: 0;
      transform: none;
      background: black;
    }

    span {
      display: block;
      position: relative;

      &::before {
        content: "";
        position: absolute;
        top: 28px;
        left: 50%;
        width: 1px;
        height: 28px;
        background: black;
      }

      &::after {
        content: "";
        position: absolute;
        top: 49px;
        left: 50%;
        width: 7px;
        height: 7px;
        border-right: 1px solid black;
        border-bottom: 1px solid black;
        transform: translateX(-50%) rotate(45deg);
        background: transparent;
      }
    }
  }
`;

const ArchitectureBranches = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  margin: 0.25rem 0;
  align-items: stretch;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 2.5rem;
  }

  & + & {
    margin-top: 1.5rem;
  }
`;

const ArchitectureBranch = styled.div`
  padding: 0.85rem 1rem;
  border: 1px solid ${boxBorder};
  border-radius: 8px;
  background: ${colors.white};
  text-align: center;
  box-sizing: border-box;

  strong {
    display: block;
    font-size: 14px;
    font-weight: 700;
  }

  small {
    ${diagramHelperText}
    margin-top: 0.2rem;
  }
`;

const Principle = styled.div`
  margin-top: 2rem;
  padding: 1.25rem;
  border-top: 1px solid ${boxBorder};
  border-bottom: 1px solid ${boxBorder};
  text-align: center;

  strong {
    display: block;
    font-family: ${fontFamily.extraBold};
    font-size: 16px;
    line-height: 1.3;

    @media only screen and (min-width: ${breakpoints.tablet}) {
      font-size: 18px;
    }
  }

  small {
    ${diagramHelperText}
    margin-top: 0.45rem;
  }
`;

const ShakespeersProjectSection = () => {
  const {
    client,
    services,
    industry,
    techStack,
    links,
    heading,
    media,
    roles,
    year,
  } = PROJECT_SHAKESPEERS;
  const heroSrc = media?.hero || media?.cover?.desktop;
  const yearLabel = year.length > 1 ? `${year[0]}-${year[1]}` : year[0];

  return (
    <>
      <PageContainer>
        <ContainerScroll
          titleComponent={<Title>{heading}</Title>}
          role={roles.join(", ")}
          year={yearLabel}
        >
          {heroSrc ? (
            <ProjectHero src={heroSrc} alt="Shakespeers landing hero" />
          ) : null}
        </ContainerScroll>

        <OverviewSection>
          <OverviewRow>
            <OverviewItem>
              <OverviewLabel>Client</OverviewLabel>
              <OverviewValue>{client.name}</OverviewValue>
            </OverviewItem>
            <OverviewItem>
              <OverviewLabel>Country</OverviewLabel>
              <OverviewValue>{client.country}</OverviewValue>
            </OverviewItem>
          </OverviewRow>
          <OverviewRow>
            <OverviewItem>
              <OverviewLabel>Team</OverviewLabel>
              <OverviewValue>Solo</OverviewValue>
            </OverviewItem>
            <OverviewItem>
              <OverviewLabel>Platform</OverviewLabel>
              <OverviewValue>Web (landing page and app)</OverviewValue>
            </OverviewItem>
          </OverviewRow>
          <OverviewRow>
            <OverviewItem>
              <OverviewLabel>Services provided</OverviewLabel>
              <OverviewValue>{services.join(", ")}</OverviewValue>
            </OverviewItem>
            <OverviewItem>
              <OverviewLabel>Industry</OverviewLabel>
              <OverviewValue>{industry}</OverviewValue>
            </OverviewItem>
          </OverviewRow>
          <OverviewItem>
            <OverviewLabel>Tech stack</OverviewLabel>
            <OverviewValue>{techStack.join(", ")}</OverviewValue>
          </OverviewItem>
          {links?.project ? (
            <OverviewItem>
              <OverviewLabel>Project link</OverviewLabel>
              <OverviewValue>
                <a
                  href={links.project}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {links.project}
                </a>
              </OverviewValue>
            </OverviewItem>
          ) : null}
        </OverviewSection>

        <Body>
          <section>
            <h4>About the product</h4>
            <p>
              ShakesPeers is an EdTech platform designed to help children
              between 10 and 13 write, read, and engage with stories in a
              moderated online community. It was founded by Elizabeth Slavitt,
              who previously worked at McKinsey &amp; Company and led teams at
              Khan Academy building educational content and learning tools. Her
              work in education earned her a place on the{" "}
              <a
                href="https://www.forbes.com/profile/elizabeth-slavitt/"
                className={getLinkClassName()}
                target="_blank"
                rel="noopener noreferrer"
              >
                Forbes 30 Under 30
              </a>{" "}
              list in Education in 2014.
            </p>
            <p>
              Elizabeth reached out to me as a Full-stack Developer to build the
              product quickly and efficiently. The target was ambitious: take
              the product from concept to its first production release in three
              months.
            </p>
            {media.community ? (
              <ContentImage
                src={media.community}
                alt="Story reading feed"
                loading="lazy"
              />
            ) : null}
          </section>

          <section>
            <h4>What I was asked to build</h4>
            <p>
              The product had to serve four very different personas — types of
              people with their own goals and access in the product — while
              keeping the experience simple for children and controlled for
              parents and moderators.
            </p>

            <PersonaGrid>
              <PersonaCard>
                <strong>Visitors</strong>
                <small>
                  Discover the product, pricing, and FAQs before signing up.
                </small>
              </PersonaCard>
              <PersonaCard>
                <strong>Parents</strong>
                <small>
                  Manage subscriptions, child accounts, and access to content.
                </small>
              </PersonaCard>
              <PersonaCard>
                <strong>Children</strong>
                <small>
                  Write, read, search, react, submit, and report content.
                </small>
              </PersonaCard>
              <PersonaCard>
                <strong>Moderators</strong>
                <small>
                  Review reports and respond to content requiring attention.
                </small>
              </PersonaCard>
            </PersonaGrid>

            <p>
              That scope meant the MVP was more than a content platform. It
              combined account management, subscriptions, writing, discovery,
              reporting, moderation, notifications, and multiple permission
              boundaries into one product.
            </p>
          </section>

          <section>
            <h4>The constraints</h4>
            <ConstraintGrid>
              <ConstraintCard>
                <strong>Speed</strong>
                <small>
                  The first production release had to be ready in three months,
                  so unnecessary infrastructure work had to be avoided.
                </small>
              </ConstraintCard>
              <ConstraintCard>
                <strong>Safety</strong>
                <small>
                  The audience was children, which made reporting, moderation,
                  access control, and content rules part of the core product.
                </small>
              </ConstraintCard>
              <ConstraintCard>
                <strong>Scope</strong>
                <small>
                  A broad MVP had to cover four personas, writing,
                  subscriptions, authentication, and the supporting backend.
                </small>
              </ConstraintCard>
            </ConstraintGrid>

            <p>
              These constraints shaped the technical approach. The objective was
              not to build every piece of infrastructure ourselves, but to
              decide where custom engineering created value and where an
              established solution could get the product to production faster.
            </p>

            <Principle>
              <strong>
                Build where the product is unique. Adopt where the problem is
                already solved.
              </strong>
              <small>
                That principle became the common thread behind the main
                technology decisions.
              </small>
            </Principle>
          </section>

          <section>
            <h4>Engineering decisions</h4>

            <StackSubheading>
              1. Choosing the full-stack foundation
            </StackSubheading>
            <p>
              The first architectural decision was how to build the application
              itself. I needed a React-based frontend, backend functionality,
              GraphQL, database access, and a deployment model that could move
              quickly enough for a three-month MVP.
            </p>

            <DecisionFlow>
              <DecisionNode>
                <strong>Three-month MVP</strong>
                <small>Frontend and backend needed to move together.</small>
              </DecisionNode>
              <DecisionArrow />
              <DecisionNode>
                <strong>Evaluate the application foundation</strong>
                <small>
                  React + Express, Next.js, Remix, and RedwoodJS were
                  considered.
                </small>
              </DecisionNode>
              <DecisionArrow />
              <DecisionNode>
                <strong>RedwoodJS</strong>
                <small>
                  React, GraphQL, Prisma, and serverless APIs in one full-stack
                  framework.
                </small>
              </DecisionNode>
            </DecisionFlow>

            <DecisionOptions>
              <DecisionOption>
                <h6>Assemble the stack</h6>
                <ul>
                  <li>React + Express, Next.js, or Remix as starting points</li>
                  <li>More flexibility around individual layer choices</li>
                  <li>More infrastructure to assemble and coordinate</li>
                  <li>Slower path for a solo three-month MVP</li>
                </ul>
              </DecisionOption>
              <DecisionOption>
                <h6>RedwoodJS</h6>
                <ul>
                  <li>React, GraphQL, Prisma, and serverless APIs together</li>
                  <li>Predictable full-stack structure out of the box</li>
                  <li>Less infrastructure to assemble before shipping</li>
                  <li>Better fit for a fast solo MVP timeline</li>
                </ul>
              </DecisionOption>
            </DecisionOptions>

            <DecisionChoice>
              <strong>Decision: RedwoodJS</strong>
              <small>
                It provided React, GraphQL, Prisma, and serverless APIs in a
                predictable structure, reducing the amount of infrastructure
                that needed to be assembled before product development could
                move at full speed.
              </small>
            </DecisionChoice>

            <p>
              The value was less about using a particular framework and more
              about reducing coordination overhead. With a solo full-stack MVP,
              having the major application layers live within one opinionated
              structure made the quickest path to production also a practical
              one.
            </p>

            <StackSubheading $spaced>
              2. Building the writing experience
            </StackSubheading>
            <p>
              Writing was the central interaction in ShakesPeers. A plain text
              box would not provide enough structure for longer stories, but
              building a rich-text editor from scratch would have consumed a
              disproportionate amount of the MVP timeline.
            </p>

            <DecisionFlow>
              <DecisionNode>
                <strong>Writing experience</strong>
                <small>
                  Stories needed structure and formatting beyond plain text.
                </small>
              </DecisionNode>
              <DecisionArrow />
              <DecisionNode>
                <strong>Build vs adopt</strong>
                <small>
                  Building an editor offered control but introduced a large
                  implementation surface.
                </small>
              </DecisionNode>
              <DecisionArrow />
              <DecisionNode>
                <strong>Compare established editors</strong>
                <small>
                  TipTap, Quill, TinyMCE, Slate, and CKEditor were considered.
                </small>
              </DecisionNode>
              <DecisionArrow />
              <DecisionNode>
                <strong>CKEditor</strong>
                <small>
                  Provided a polished writing experience without requiring
                  editor infrastructure to be rebuilt.
                </small>
              </DecisionNode>
            </DecisionFlow>

            <DecisionOptions>
              <DecisionOption>
                <h6>Build an editor internally</h6>
                <ul>
                  <li>Maximum control</li>
                  <li>Significant implementation effort</li>
                  <li>More edge cases to own and maintain</li>
                  <li>Poor fit for a three-month MVP</li>
                </ul>
              </DecisionOption>
              <DecisionOption>
                <h6>Use an established editor</h6>
                <ul>
                  <li>Faster path to a usable writing experience</li>
                  <li>Mature formatting behavior</li>
                  <li>Less infrastructure to maintain</li>
                  <li>More time available for the product experience</li>
                </ul>
              </DecisionOption>
            </DecisionOptions>

            <DecisionChoice>
              <strong>Decision: CKEditor</strong>
              <small>
                CKEditor gave the product a polished editor quickly enough to
                keep the focus on the storytelling experience rather than
                recreating the underlying editor.
              </small>
            </DecisionChoice>

            <p>
              The editor became the writing surface for children's stories,
              giving them structured formatting while keeping the experience
              much closer to a familiar writing tool than a plain text field.
            </p>

            {media.writing ? (
              <ContentImage
                src={media.writing}
                alt="Story editor"
                loading="lazy"
              />
            ) : null}

            <StackSubheading $spaced>
              3. Getting authentication out of the way
            </StackSubheading>
            <p>
              Authentication was required from the beginning because parents,
              children, and moderators had different levels of access. At the
              same time, authentication itself was not the product's
              differentiator.
            </p>

            <DecisionFlow>
              <DecisionNode>
                <strong>Multiple persona types</strong>
                <small>
                  Different accounts and protected workflows from day one.
                </small>
              </DecisionNode>
              <DecisionArrow />
              <DecisionNode>
                <strong>Build authentication vs use a provider</strong>
                <small>
                  Control and ownership had to be weighed against MVP speed and
                  maintenance.
                </small>
              </DecisionNode>
              <DecisionArrow />
              <DecisionNode>
                <strong>Clerk</strong>
                <small>
                  Fast path to established authentication infrastructure.
                </small>
              </DecisionNode>
            </DecisionFlow>

            <DecisionOptions>
              <DecisionOption>
                <h6>Build internally</h6>
                <ul>
                  <li>Complete control over implementation</li>
                  <li>More security-sensitive infrastructure to own</li>
                  <li>More edge cases and maintenance</li>
                  <li>Slower path to the MVP</li>
                </ul>
              </DecisionOption>
              <DecisionOption>
                <h6>Use Clerk</h6>
                <ul>
                  <li>Fast integration</li>
                  <li>Established authentication flows</li>
                  <li>Less infrastructure to maintain</li>
                  <li>Better aligned with an early-stage MVP timeline</li>
                </ul>
              </DecisionOption>
            </DecisionOptions>

            <DecisionChoice>
              <strong>Decision: Clerk</strong>
              <small>
                For an early-stage product, I preferred to use an established
                authentication provider rather than spend the limited MVP
                timeline building authentication infrastructure that was not a
                differentiating part of ShakesPeers.
              </small>
            </DecisionChoice>
          </section>

          <section>
            <h4>Safety as a product workflow</h4>

            <p>
              Because children were creating and sharing content, moderation
              could not be treated as a secondary administrative feature. It had
              to be part of the product flow itself.
            </p>

            <ArchitectureDiagram>
              <ArchitectureNode>
                <strong>Child creates or reads content</strong>
                <small>Writing, submission, discovery, and reactions.</small>
              </ArchitectureNode>
              <ArchitectureArrow />
              <ArchitectureBranches>
                <ArchitectureBranch>
                  <strong>Report</strong>
                  <small>
                    A child can flag content that feels inappropriate.
                  </small>
                </ArchitectureBranch>
                <ArchitectureBranch>
                  <strong>Blacklist detection</strong>
                  <small>
                    Words and emojis can trigger moderator attention.
                  </small>
                </ArchitectureBranch>
                <ArchitectureBranch>
                  <strong>Normal flow</strong>
                  <small>
                    Content continues through the regular product experience.
                  </small>
                </ArchitectureBranch>
              </ArchitectureBranches>
              <ArchitectureArrow />
              <ArchitectureNode>
                <strong>Moderator review</strong>
                <small>
                  Moderators receive alerts and review content requiring
                  attention.
                </small>
              </ArchitectureNode>
            </ArchitectureDiagram>

            <p>
              Moderators had access to the blacklist and alert automations,
              while parents and children could submit reports and suggestions
              without accessing moderation controls. AWS SNS was used to notify
              moderators when moderation required attention.
            </p>

            {media.moderation ? (
              <ContentImage
                src={media.moderation}
                alt="Moderator reports view"
                loading="lazy"
              />
            ) : null}
          </section>

          <section>
            <h4>Resulting architecture</h4>

            <p>
              The individual choices were driven by the same constraint: reduce
              infrastructure work where an established solution was already
              strong, and reserve custom engineering for the product's own
              workflows.
            </p>

            <ArchitectureDiagram>
              <ArchitectureNode>
                <strong>ShakesPeers</strong>
                <small>
                  Product experience across visitors, parents, children, and
                  moderators.
                </small>
              </ArchitectureNode>
              <ArchitectureSplitArrow aria-hidden="true">
                <span />
                <span />
                <span />
              </ArchitectureSplitArrow>
              <ArchitectureBranches>
                <ArchitectureBranch>
                  <strong>RedwoodJS</strong>
                  <small>
                    React application, GraphQL, Prisma, and serverless backend
                    structure.
                  </small>
                </ArchitectureBranch>
                <ArchitectureBranch>
                  <strong>Clerk</strong>
                  <small>Authentication and protected user workflows.</small>
                </ArchitectureBranch>
                <ArchitectureBranch>
                  <strong>CKEditor</strong>
                  <small>
                    Structured writing experience for children's stories.
                  </small>
                </ArchitectureBranch>
              </ArchitectureBranches>
              <ArchitectureBranches>
                <ArchitectureBranch>
                  <strong>PostgreSQL + Prisma</strong>
                  <small>
                    Connected data across users, stories, subscriptions, and
                    moderation.
                  </small>
                </ArchitectureBranch>
                <ArchitectureBranch>
                  <strong>Stripe</strong>
                  <small>Parent checkout and recurring subscriptions.</small>
                </ArchitectureBranch>
                <ArchitectureBranch>
                  <strong>AWS / Vercel / GitHub Actions</strong>
                  <small>
                    Serverless infrastructure and automated delivery.
                  </small>
                </ArchitectureBranch>
              </ArchitectureBranches>
            </ArchitectureDiagram>
          </section>

          <section>
            <h4>What was shipped</h4>

            <p>
              Visitors could discover the product, review FAQs and pricing, and
              register as parents through Stripe checkout. Parents could create
              child accounts, send invitation emails, manage subscriptions, and
              browse content.
            </p>

            {media.parents ? (
              <ContentImage
                src={media.parents}
                alt="Parent dashboard with child accounts"
                loading="lazy"
              />
            ) : null}

            <p>
              Children received their own writing and reading space, with daily
              prompts, a seven-day submission workflow, topic search, emoji
              reactions, and reporting. Image uploads were deliberately left out
              to keep the experience clean and reduce privacy risk.
            </p>

            {media.submission ? (
              <ContentImage
                src={media.submission}
                alt="Writing submission flow"
                loading="lazy"
              />
            ) : null}

            <p>
              Moderators could review reported content and receive automated
              alerts when blacklist rules or new reports required attention.
            </p>
          </section>

          <section>
            <h4>Outcome</h4>
            <p>
              The first production release shipped in three months, taking
              ShakesPeers from concept to a working full-stack product across
              four distinct persona experiences.
            </p>
            <p>
              The result was a production MVP that combined writing,
              subscriptions, authentication, content discovery, reporting, and
              moderation without requiring the core infrastructure behind each
              capability to be built from scratch.
            </p>
          </section>

          <section>
            <h4>Testimonials</h4>
            <Testimonials showMore={false} reviews={client.reviews} />
          </section>
        </Body>
      </PageContainer>
      <ContactMeSection />
    </>
  );
};

export default ShakespeersProjectSection;
